import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Delete,
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/ar';
import 'moment/locale/en-gb';
import { theme } from '../../../theme';
import Can from '../../../permissions/Can';
import MarkAttendance from './MarkAttendance';
import { useTranslation } from 'react-i18next';

const DayView = ({
  appointments = [],
  currentDate,
  onShowDetails,
  onEditAppointment,
  onDeleteAppointment,
  isEmployee = false,
}) => {
  const { t, i18n } = useTranslation();

  // Configure moment locale immediately and on language change
  const setMomentLocale = () => {
    const momentLocale = i18n.language === 'ar' ? 'ar' : i18n.language === 'en' ? 'en-gb' : 'fr';
    moment.locale(momentLocale);
    
    // For Arabic, use Latin numerals instead of Arabic numerals
    if (i18n.language === 'ar') {
      moment.updateLocale(momentLocale, {
        preparse: function (string) {
          return string.replace(/[٠-٩]/g, function (match) {
            return match.charCodeAt(0) - 0x0660;
          });
        },
        postformat: function (string) {
          return string.replace(/\d/g, function (match) {
            return String.fromCharCode(match.charCodeAt(0) + 0x0660);
          });
        }
      });
      // Override postformat to keep Latin numerals
      moment.updateLocale(momentLocale, {
        postformat: function (string) {
          return string; // Keep original string with Latin numerals
        }
      });
    }
  };
  
  // Set locale immediately on mount
  setMomentLocale();
  
  // Also update locale when language changes
  useEffect(() => {
    setMomentLocale();
  }, [i18n.language]);

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);
  const [selectedAppointmentForAction, setSelectedAppointmentForAction] = useState(null);
  const [selectedDayAppointments, setSelectedDayAppointments] = useState([]);

  const START_HOUR = 6;
  const END_HOUR = 21;
  const hours = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => i + START_HOUR);
  const HOUR_HEIGHT = 60;

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return theme.colors.success[500];
      case 'pending': return theme.colors.warning[500];
      case 'cancelled': return theme.colors.error[500];
      case 'completed': return theme.colors.info[500];
      default: return theme.colors.primary[500];
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed': return t('pro_dashboard.status.confirmed');
      case 'pending': return t('pro_dashboard.status.pending');
      case 'cancelled': return t('pro_dashboard.status.cancelled');
      case 'completed': return t('pro_dashboard.status.completed');
      default: return status;
    }
  };

  const positionedAppointments = useMemo(() => {
    const currentDateKey = currentDate.format('YYYY-MM-DD');
    return appointments
      .filter(a => moment(a.start_time).format('YYYY-MM-DD') === currentDateKey)
      .map(a => {
        const start = moment(a.start_time);
        const end = moment(a.end_time);
        if (!start.isValid() || !end.isValid()) return null;

        const duration = end.diff(start, 'minutes');
        const top = ((start.hour() - START_HOUR) + start.minute() / 60) * HOUR_HEIGHT;
        const height = Math.max((duration / 60) * HOUR_HEIGHT, 30);

        return { ...a, topPosition: top, height, durationMinutes: duration };
      })
      .filter(Boolean);
  }, [appointments, currentDate]);

  // Afficher la grille horaire sur mobile aussi, avec la dialog en overlay
  if (isMobile) {
    return (
      <Box sx={{ backgroundColor: '#fff', borderRadius: 3, overflow: 'auto', border: `1px solid ${theme.colors.primary[100]}`, height: 'calc(100vh - 280px)' }}>
        {/* Grille horaire mobile */}
        <Box sx={{ display: 'flex' }}>
          {/* HEURES */}
          <Box sx={{ width: '50px', flexShrink: 0, position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 5, borderRight: `1px solid ${theme.colors.primary[200]}` }}>
            {hours.map((h, i) => (
              <Box key={h} sx={{ position: 'absolute', top: `${i * HOUR_HEIGHT}px`, left: 0, right: 0, height: HOUR_HEIGHT, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 0.5 }}>
                <Typography sx={{ fontSize: '0.6rem', color: theme.colors.text.secondary, fontWeight: 500 }}>{`${h}:00`}</Typography>
              </Box>
            ))}
          </Box>

          {/* RDV */}
          <Box sx={{ flex: 1, position: 'relative', minHeight: `${HOUR_HEIGHT * hours.length}px`, backgroundColor: '#fafafa' }}>
            {hours.map((h, i) => <Box key={`line-${h}`} sx={{ position: 'absolute', top: `${i * HOUR_HEIGHT}px`, left: 0, right: 0, borderTop: `1px solid ${theme.colors.primary[200]}`, pointerEvents: 'none', zIndex: 1 }} />)}
            {hours.slice(0, -1).map((h, i) => <Box key={`half-${h}`} sx={{ position: 'absolute', top: `${(i + 1) * HOUR_HEIGHT - 1}px`, left: 0, right: 0, borderTop: `1px dashed ${theme.colors.primary[100]}`, pointerEvents: 'none', zIndex: 1 }} />)}

            {positionedAppointments.map((a, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  top: `${a.topPosition}px`,
                  left: '4px',
                  right: '4px',
                  height: `${a.height}px`,
                  minHeight: 24,
                  backgroundColor: getStatusColor(a.status),
                  border: `1px solid ${getStatusColor(a.status)}`,
                  borderLeft: `3px solid ${getStatusColor(a.status)}`,
                  borderRadius: 1,
                  p: 0.3,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  zIndex: 2,
                  '&:hover': { boxShadow: '0 3px 10px rgba(0,0,0,0.2)', zIndex: 10 },
                }}
                onClick={() => {
                  setSelectedDayAppointments([a]);
                  setSelectedAppointmentForAction(a);
                  setMobileDialogOpen(true);
                }}
              >
                <Box
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    borderRadius: 0.5,
                    p: 0.2,
                    height: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: '0.65rem', color: theme.colors.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center', width: '100%' }}>
                    {a.client_name}
                  </Typography>
                  {a.height > 30 && (
                    <Typography sx={{ fontSize: '0.55rem', color: theme.colors.text.secondary }}>
                      {moment(a.start_time).format('HH:mm')}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Dialog mobile */}
        <Dialog
          open={mobileDialogOpen}
          onClose={() => {
            setMobileDialogOpen(false);
            setSelectedAppointmentForAction(null);
          }}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-container': {
              alignItems: 'center',
              justifyContent: 'center',
            },
            '& .MuiDialog-paper': {
              m: { xs: 0, md: 2 },
              maxHeight: { xs: '100%', md: 'calc(100% - 64px)' },
              width: { xs: '100%', md: 'auto' },
              maxWidth: { xs: '100%', md: '600px' },
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 700, 
            borderBottom: `2px solid ${theme.colors.primary[100]}`,
          }}>
            {t('pro_dashboard.appointments.title')}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {selectedDayAppointments.map((appointment, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  border: `2px solid ${getStatusColor(appointment.status)}`,
                  backgroundColor: '#f9f9f9',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: theme.colors.primary[50],
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }
                }}
              >
                {/* Boutons d'action en haut à droite */}
                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                  <MarkAttendance 
                    appointment={appointment} 
                    onUpdate={() => {
                      setMobileDialogOpen(false);
                      setSelectedAppointmentForAction(null);
                      window.location.reload();
                    }} 
                  />
                  {!isEmployee && (
                    <>
                      <Tooltip title={t('actions.edit')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditAppointment?.(appointment);
                            setMobileDialogOpen(false);
                            setSelectedAppointmentForAction(null);
                          }}
                          sx={{
                            color: theme.colors.primary[500],
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: theme.colors.primary[500],
                              color: '#fff',
                            }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('actions.delete')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteAppointment?.(appointment);
                            setMobileDialogOpen(false);
                            setSelectedAppointmentForAction(null);
                          }}
                          sx={{
                            color: theme.colors.error[500],
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: theme.colors.error[500],
                              color: '#fff',
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
                
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, pr: 8 }}>
                  {moment(appointment.start_time).format('HH:mm')} - {appointment.client_name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{t('pro_dashboard.services.title')}:</strong> {appointment.service_name}
                </Typography>
                {appointment.team_member_name && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>{t('pro_dashboard.team.title')}:</strong> {appointment.team_member_name}
                  </Typography>
                )}
                {appointment.salon_name && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>{t('pro_dashboard.salon.title')}:</strong> {appointment.salon_name}
                  </Typography>
                )}
                <Chip
                  label={getStatusLabel(appointment.status)}
                  size="small"
                  sx={{
                    backgroundColor: getStatusColor(appointment.status),
                    color: 'white',
                    mt: 1,
                  }}
                />
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setMobileDialogOpen(false);
              setSelectedAppointmentForAction(null);
            }}>{t('actions.close')}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Vue desktop - grille horaire
  return (
    <Box sx={{ backgroundColor: '#fff', borderRadius: 3, overflow: 'auto', border: `1px solid ${theme.colors.primary[100]}`, height: 'calc(100vh - 280px)' }}>
      <Box sx={{ display: 'flex' }}>
        {/* HEURES */}
        <Box sx={{ width: '80px', flexShrink: 0, position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 10, borderRight: `1px solid ${theme.colors.primary[200]}` }}>
          {hours.map((h, i) => (
            <Box key={h} sx={{ position: 'absolute', top: `${i * HOUR_HEIGHT}px`, left: 0, right: 0, height: HOUR_HEIGHT, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 0.5 }}>
              <Typography sx={{ fontSize: '0.75rem', color: theme.colors.text.secondary, fontWeight: 500 }}>{`${h}:00`}</Typography>
            </Box>
          ))}
        </Box>

        {/* CALENDRIER */}
        <Box sx={{ flex: 1, position: 'relative', minHeight: `${HOUR_HEIGHT * hours.length}px`, backgroundColor: '#fafafa' }}>
          {hours.map((h, i) => (
            <Box key={`line-${h}`} sx={{ position: 'absolute', top: `${i * HOUR_HEIGHT}px`, left: 0, right: 0, borderTop: `1px solid ${theme.colors.primary[200]}`, pointerEvents: 'none', zIndex: 1 }} />
          ))}
          {hours.slice(0, -1).map((h, i) => (
            <Box key={`half-${h}`} sx={{ position: 'absolute', top: `${(i + 1) * HOUR_HEIGHT - 1}px`, left: 0, right: 0, borderTop: `1px dashed ${theme.colors.primary[100]}`, pointerEvents: 'none', zIndex: 1 }} />
          ))}

          {positionedAppointments.map((a, i) => (
            <Tooltip
              key={`${a.appointment_id}-${i}`}
              title={
                <Box sx={{ p: 1.5, minWidth: 240, fontFamily: 'Roboto, sans-serif' }}>
                  {/* CLIENT */}
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: '#fff',
                      fontSize: '1rem',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.8,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 18 }} />
                    {a.client_name}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

                    {/* SERVICE */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 4, height: 4, bgcolor: '#fff', borderRadius: '50%' }} />
                      <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                        <strong>{t('pro_dashboard.services.title')}:</strong> {a.service_name}
                      </Typography>
                    </Box>

                    {/* TÉLÉPHONE CLIENT */}
                    {a.client_phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon sx={{ fontSize: 14, color: '#ffcc80' }} />
                        <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                          <strong>{t('pro_dashboard.client.phone')}:</strong> {a.client_phone}
                        </Typography>
                      </Box>
                    )}

                    {/* EMPLOYÉ */}
                    {a.team_member_name && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: 14, color: '#90caf9' }} />
                        <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                          <strong>{t('pro_dashboard.team.title')}:</strong> {a.team_member_name}
                        </Typography>
                      </Box>
                    )}

                    {/* SALON */}
                    {a.salon_name && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon sx={{ fontSize: 14, color: '#a5d6a7' }} />
                        <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                          <strong>{t('pro_dashboard.salon.title')}:</strong> {a.salon_name}
                        </Typography>
                      </Box>
                    )}

                    {/* HORAIRE */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 28,
                        height: 28,
                        bgcolor: '#fff',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#333',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                      }}>
                        {moment(a.start_time).format('HH')}
                      </Box>
                      <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 500 }}>
                        {moment(a.start_time).format('HH:mm')} → {moment(a.end_time).format('HH:mm')}
                      </Typography>
                    </Box>

                    {/* DURÉE */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`${a.durationMinutes} min`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: '#fff',
                          fontWeight: 600,
                          height: 22,
                          fontSize: '0.75rem',
                        }}
                      />
                      <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                        <strong>{t('pro_dashboard.appointments.duration')}</strong>
                      </Typography>
                    </Box>

                    {/* STATUT */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: getStatusColor(a.status),
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        {getStatusLabel(a.status)}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              }
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1a1a1a',
                    color: '#fff',
                    borderRadius: 2,
                    boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                    border: '1px solid #333',
                    p: 0,
                  },
                },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: `${a.topPosition}px`,
                  left: '8px',
                  right: '8px',
                  height: `${a.height}px`,
                  minHeight: 30,
                  backgroundColor: getStatusColor(a.status),
                  border: `1px solid ${getStatusColor(a.status)}`,
                  borderLeft: `4px solid ${getStatusColor(a.status)}`,
                  borderRadius: 1,
                  p: 0.5,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  zIndex: 2,
                  '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 10, '& .actions': { opacity: 1 } },
                }}
                onClick={() => onShowDetails?.(a)}
              >
                <Box
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    borderRadius: 0.5,
                    p: 0.5,
                    height: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: a.durationMinutes <= 30 ? 'center' : 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  {/* ≤ 30 MIN → CENTRÉ */}
                  {a.durationMinutes <= 30 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4, flexWrap: 'nowrap', width: '100%' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', color: theme.colors.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, textAlign: 'center' }}>
                        {a.client_name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: theme.colors.text.secondary }}>
                        • {a.service_name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: theme.colors.text.secondary }}>
                        {moment(a.start_time).format('HH:mm')}
                      </Typography>
                      <Chip label={`${a.durationMinutes}m`} size="small" sx={{ height: 14, fontSize: '0.5rem', backgroundColor: theme.colors.grey[100], color: theme.colors.text.secondary }} />
                      {a.team_member_name && (
                        <Chip
                          icon={<PersonIcon sx={{ fontSize: 9 }} />}
                          label={a.team_member_name.split(' ')[0]}
                          size="small"
                          sx={{ height: 14, fontSize: '0.5rem', backgroundColor: theme.colors.primary[100], color: theme.colors.primary[700] }}
                        />
                      )}
                    </Box>
                  ) : (
                    /* > 30 MIN → 2 LIGNES */
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.4, flexWrap: 'nowrap', width: '100%' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: theme.colors.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                          {a.client_name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.2, alignItems: 'center', flexShrink: 0 }}>
                          <Typography sx={{ fontSize: '0.65rem', color: theme.colors.text.secondary }}>
                            <Typography variant="caption">{t('pro_dashboard.appointments.time')}: {moment(a.start_time).format('HH:mm')} - {moment(a.end_time).format('HH:mm')}</Typography>
                          </Typography>
                          <Chip label={`${a.durationMinutes} min`} size="small" sx={{ height: 16, fontSize: '0.6rem', backgroundColor: theme.colors.grey[100], color: theme.colors.text.secondary }} />
                          <Chip label={getStatusLabel(a.status)} size="small" sx={{ height: 16, fontSize: '0.6rem', backgroundColor: getStatusColor(a.status), color: 'white' }} />
                        </Box>
                      </Box>

                      {a.height > 50 && (
                        <Box sx={{ display: 'flex', gap: 0.4, mt: 0.2, justifyContent: 'center', flexWrap: 'wrap', width: '100%' }}>
                          <Typography sx={{ fontSize: '0.7rem', color: theme.colors.text.secondary }}>
                            {a.service_name}
                          </Typography>
                          {a.team_member_name && (
                            <Chip
                              icon={<PersonIcon sx={{ fontSize: 9 }} />}
                              label={a.team_member_name}
                              size="small"
                              sx={{ height: 16, fontSize: '0.6rem', backgroundColor: theme.colors.primary[100], color: theme.colors.primary[700] }}
                            />
                          )}
                          {a.salon_name && (
                            <Chip
                              icon={<BusinessIcon sx={{ fontSize: 9 }} />}
                              label={a.salon_name}
                              size="small"
                              sx={{ height: 16, fontSize: '0.6rem', backgroundColor: theme.colors.secondary[100], color: theme.colors.secondary[700] }}
                            />
                          )}
                        </Box>
                      )}
                    </>
                  )}

                  {/* BOUTONS */}
                  <Box className="actions" sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.2, opacity: 0, transition: 'opacity 0.2s' }}>
                    <MarkAttendance appointment={a} onUpdate={() => window.location.reload()} />
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onShowDetails?.(a); }} sx={{ p: '1.5px', backgroundColor: 'rgba(255,255,255,0.95)', width: 18, height: 18, color: theme.colors.primary[600], '&:hover': { backgroundColor: theme.colors.primary[50] } }}>
                      <Visibility sx={{ fontSize: 12 }} />
                    </IconButton>
                    <Can I="update" a="appointments">
                      {!isEmployee && <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEditAppointment?.(a); }} sx={{ p: '1.5px', backgroundColor: 'rgba(255,255,255,0.95)', width: 18, height: 18, color: theme.colors.warning[600], '&:hover': { backgroundColor: theme.colors.warning[50] } }}>
                        <Edit sx={{ fontSize: 12 }} />
                      </IconButton>}
                    </Can>
                    <Can I="delete" a="appointments">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDeleteAppointment?.(a); }} sx={{ p: '1.5px', backgroundColor: 'rgba(255,255,255,0.95)', width: 18, height: 18, color: theme.colors.error[600], '&:hover': { backgroundColor: theme.colors.error[50] } }}>
                        <Delete sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Can>
                  </Box>
                </Box>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default DayView;